<template>
  <div class="dashboard-body">
    <div class="row">
      <sidenav class="custom-col-width-2" />
      <div class="custom-col-width-10">
        <navbar />
        <div class="content-section">
          <section class="list-page">
            <header class="list-page__head">
              <div class="list-page__heading">
                <h2 class="list-page__title">Subscription Plans</h2>
                <p class="list-page__count">{{ countLabel }}</p>
              </div>
              <div class="head-actions">
                <button type="button" class="head-refresh" :disabled="isLoading" v-on:click="loadPlans" aria-label="Refresh">
                  <i :class="['pi', isLoading ? 'pi-spin pi-spinner' : 'pi-refresh']"></i>
                </button>
                <button type="button" class="list-page__action" v-on:click="openCreate">
                  <i class="pi pi-plus"></i>
                  <span>New Plan</span>
                </button>
              </div>
            </header>

            <div class="list-page__table">
              <tableLoader v-if="isLoading" message="Loading plans…" />
              <div v-if="!isLoading" class="table-scroller">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Name</th>
                      <th scope="col">Price</th>
                      <th scope="col">Duration</th>
                      <th scope="col">Verifications</th>
                      <th scope="col">Seats</th>
                      <th scope="col">Selectable</th>
                      <th scope="col">Status</th>
                      <th scope="col" class="col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(plan, index) in plans" v-bind:key="plan.id || index">
                      <th scope="row">{{ index + 1 }}</th>
                      <td class="row-name">
                        {{ plan.name }}
                        <span v-if="plan.isFree" class="badge badge-info plan-free">Free</span>
                      </td>
                      <td class="amount-cell">{{ money(plan.totalPrice) }}</td>
                      <td>{{ plan.duration }} day{{ plan.duration === 1 ? '' : 's' }}</td>
                      <td>{{ verificationLimitLabel(plan) }}</td>
                      <td>{{ seatsLabel(plan) }}</td>
                      <td>
                        <span v-if="plan.isUserSelectable" class="badge badge-success">Yes</span>
                        <span v-else class="badge badge-secondary">No</span>
                      </td>
                      <td>
                        <span v-if="plan.isActive" class="badge badge-success">Active</span>
                        <span v-else class="badge badge-danger">Inactive</span>
                      </td>
                      <td class="col-actions">
                        <div class="dropdown dropleft">
                          <div class="dropdown-toggle actions-dropdown" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            <i class="pi pi-ellipsis-h"></i>
                          </div>
                          <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                            <a class="dropdown-item" v-on:click="openEdit(plan)">Edit</a>
                            <a class="dropdown-item" v-on:click="toggleActive(plan)">{{ plan.isActive ? 'Deactivate' : 'Activate' }}</a>
                            <a class="dropdown-item final-item text-danger" v-on:click="confirmDelete(plan)">Delete</a>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="!isLoading && plans.length === 0" class="list-page__empty">
                <i class="pi pi-layer-group"></i>
                <div>No subscription plans yet. Create your first plan.</div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>

    <subscriptionPlanModal
      v-if="showPlanModal"
      :plan="editingPlan"
      @close="showPlanModal = false"
      @saved="onSaved"
    />

    <deleteConfirmationModal
      v-if="showDeleteModal"
      :delete-function="deletePlan"
      title="Delete plan"
      message="Are you sure you want to delete this subscription plan?"
      :record-label="selectedPlan ? selectedPlan.name : ''"
      @close-delete-confirmation-modal="showDeleteModal = false"
      @refresh-records="onDeleted"
    />
  </div>
</template>
<script>
import sidenav from "@/components/Sidenav.vue";
import navbar from "@/components/Navbar.vue";
import tableLoader from "@/components/Table_Loader.vue";
import deleteConfirmationModal from "@/components/Delete_Confirmation_Modal.vue";
import subscriptionPlanModal from "@/components/subscription/Subscription_Plan_Modal.vue";
import Subscription from "@/services/subscription";
import { DEFAULT_CURRENCY, TOAST_GLOBAL_CONFIG } from "@/utils/const";

export default {
  title: "Subscription Plans",
  components: {
    sidenav,
    navbar,
    tableLoader,
    deleteConfirmationModal,
    subscriptionPlanModal,
  },
  data() {
    return {
      plans: [],
      isLoading: false,
      showPlanModal: false,
      editingPlan: null,
      showDeleteModal: false,
      selectedPlan: null,
      togglingId: null,
    };
  },
  computed: {
    countLabel() {
      const total = this.plans.length;
      return total === 1 ? "1 plan" : `${total} plans`;
    },
  },
  methods: {
    money(value) {
      const amount = Number(value);
      if (isNaN(amount)) return "—";
      return this.$filters.formatPrice(amount, DEFAULT_CURRENCY);
    },
    verificationLimitLabel(plan) {
      if (plan.verificationLimit == null) return "Unlimited";
      const period = plan.limitPeriod === "total" ? "lifetime" : plan.limitPeriod || "daily";
      return `${plan.verificationLimit} / ${period}`;
    },
    seatsLabel(plan) {
      return plan.maxUsers == null ? "Unlimited" : plan.maxUsers;
    },
    async loadPlans() {
      this.isLoading = true;
      try {
        const res = await Subscription.getSubscriptionPlans({ limit: 200 });
        if (res?.data?.status) {
          this.plans = res.data.data || [];
        }
      } finally {
        this.isLoading = false;
      }
    },
    openCreate() {
      this.editingPlan = null;
      this.showPlanModal = true;
    },
    openEdit(plan) {
      this.editingPlan = plan;
      this.showPlanModal = true;
    },
    onSaved() {
      this.loadPlans();
    },
    async toggleActive(plan) {
      if (this.togglingId) return;
      this.togglingId = plan.id;
      try {
        const res = await Subscription.togglePlanActive({ id: plan.id });
        if (res?.data?.status) {
          this.$toast.success(plan.isActive ? "Plan deactivated." : "Plan activated.", TOAST_GLOBAL_CONFIG);
          await this.loadPlans();
        }
      } finally {
        this.togglingId = null;
      }
    },
    confirmDelete(plan) {
      this.selectedPlan = plan;
      this.showDeleteModal = true;
    },
    deletePlan() {
      return Subscription.deleteSubscriptionPlan(this.selectedPlan.id);
    },
    onDeleted() {
      this.$toast.success("Plan deleted.", TOAST_GLOBAL_CONFIG);
      this.loadPlans();
    },
  },
  mounted() {
    this.loadPlans();
  },
};
</script>
<style scoped>
.head-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.head-refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-md);
  background: var(--ehs-color-surface);
  color: var(--ehs-color-muted);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.head-refresh:hover:not(:disabled) {
  background: var(--ehs-color-canvas);
  color: var(--ehs-color-ink);
}

.amount-cell {
  font-weight: 600;
  color: var(--ehs-color-ink);
}

.plan-free {
  margin-left: 8px;
  text-transform: none;
}
</style>
